import { useEffect, useRef, useState } from "react";
import { DatabaseBackup, Loader2, ShieldCheck, Clock, HardDriveDownload, RotateCcw, Play, FileDown, Mail, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api, { formatApiError } from "../api";

const tipeBadge = {
  harian: "bg-blue-100 text-blue-800 border border-blue-200",
  bulanan: "bg-purple-100 text-purple-800 border border-purple-200",
  manual: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  "pra-restore": "bg-amber-100 text-amber-800 border border-amber-200",
};
const tipeLabel = { harian: "Harian", bulanan: "Bulanan", manual: "Manual", "pra-restore": "Pra-Restore" };
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function BackupPage() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [emailing, setEmailing] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/backup").then(({ data }) => setData(data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  const runBackup = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/backup/run");
      toast.success(`Backup berhasil dibuat: ${data.total_dokumen} dokumen tersimpan terenkripsi`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const doRestore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post("/backup/restore", { filename: restoreTarget.filename });
      toast.success(`Restore selesai — ${data.restored_docs} dokumen dipulihkan dari ${data.restored_collections} koleksi. Backup pengaman: ${data.safety_backup}`, { duration: 8000 });
      setRestoreTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRestoring(false);
    }
  };

  const download = async (filename) => {
    try {
      const res = await api.get(`/backup/${filename}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("File backup terenkripsi diunduh");
    } catch {
      toast.error("Gagal mengunduh backup");
    }
  };

  const emailBackup = async (filename) => {
    setEmailing(filename);
    try {
      const { data } = await api.post(`/backup/${filename}/email`);
      toast.success(`Backup terkirim ke email ${data.recipient}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEmailing(null);
    }
  };

  const doUploadRestore = async () => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const { data } = await api.post("/backup/restore-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Restore dari unggahan selesai — ${data.restored_docs} dokumen dipulihkan dari ${data.restored_collections} koleksi. Backup pengaman: ${data.safety_backup}`, { duration: 8000 });
      setUploadFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="backup-page" className="space-y-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <DatabaseBackup className="w-7 h-7 text-emerald-700" /> Backup &amp; Restore Data
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Pencadangan otomatis seluruh database — terenkripsi, dengan kebijakan retensi</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <input ref={fileRef} type="file" accept=".enc" className="hidden" data-testid="restore-upload-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setUploadFile(f); }} />
          <Button data-testid="restore-upload-button" variant="outline" onClick={() => fileRef.current?.click()}
            className="border-amber-300 text-amber-800 hover:bg-amber-50 btn-touch">
            <Upload className="w-4 h-4 mr-2" /> Pulihkan dari File
          </Button>
          <Button data-testid="run-backup-button" onClick={runBackup} disabled={running}
            className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 btn-touch">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Backup Sekarang
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-5 flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">Jadwal Otomatis</p>
              <p className="text-sm font-semibold text-zinc-900 mt-0.5">Setiap hari 23:59 WIB</p>
              <p data-testid="next-run-label" className="text-xs text-zinc-500 mt-0.5">Berikutnya: {data?.next_run_wib || "..."}</p>
              <p className="text-xs text-zinc-500 mt-0.5">Tiap Minggu malam dikirim ke email pemilik</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-5 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">Enkripsi</p>
              <p className="text-sm font-semibold text-zinc-900 mt-0.5">AES (Fernet) — file .enc</p>
              <p className="text-xs text-zinc-500 mt-0.5">Hanya bisa dibuka dengan kunci server</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-5 flex items-start gap-3">
            <HardDriveDownload className="w-5 h-5 text-purple-700 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">Retensi</p>
              <p className="text-sm font-semibold text-zinc-900 mt-0.5">Harian 30 hari • Bulanan 12 bulan</p>
              <p className="text-xs text-zinc-500 mt-0.5">Backup lama dihapus otomatis</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold tracking-tight">Riwayat Backup</CardTitle>
        </CardHeader>
        <CardContent>
          {data === null ? (
            <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : data.items.length === 0 ? (
            <p data-testid="backup-empty" className="text-sm text-zinc-400 text-center py-8">
              Belum ada backup. Klik "Backup Sekarang" atau tunggu jadwal otomatis 23:59 WIB.
            </p>
          ) : (
            <div className="divide-y divide-zinc-100" data-testid="backup-list">
              {data.items.map((b) => (
                <div key={b.filename} data-testid={`backup-row-${b.filename}`}
                  className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${tipeBadge[b.tipe] || ""} font-medium`}>{tipeLabel[b.tipe] || b.tipe}</Badge>
                      <span className="text-sm font-medium text-zinc-900">{b.waktu_wib}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 truncate">
                      {b.filename} • {fmtSize(b.size_bytes)}
                      {b.total_dokumen != null && ` • ${b.total_dokumen.toLocaleString("id-ID")} dokumen`}
                      {b.oleh && b.oleh !== "-" && ` • oleh ${b.oleh}`}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    <Button data-testid={`download-backup-${b.filename}`} variant="outline" size="sm" onClick={() => download(b.filename)}
                      className="border-zinc-300 text-zinc-700 hover:bg-zinc-50 btn-touch">
                      <FileDown className="w-4 h-4 mr-1.5" /> Unduh
                    </Button>
                    <Button data-testid={`email-backup-${b.filename}`} variant="outline" size="sm"
                      onClick={() => emailBackup(b.filename)} disabled={emailing === b.filename}
                      className="border-sky-300 text-sky-800 hover:bg-sky-50 btn-touch">
                      {emailing === b.filename ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />} Email
                    </Button>
                    <Button data-testid={`restore-backup-${b.filename}`} variant="outline" size="sm" onClick={() => setRestoreTarget(b)}
                      className="border-amber-300 text-amber-800 hover:bg-amber-50 btn-touch">
                      <RotateCcw className="w-4 h-4 mr-1.5" /> Pulihkan
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!restoreTarget} onOpenChange={(open) => !open && setRestoreTarget(null)}>
        <AlertDialogContent className="bg-white" data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pulihkan Data dari Backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Seluruh data saat ini akan <b>DIGANTI</b> dengan isi backup <b>{restoreTarget?.waktu_wib}</b> ({tipeLabel[restoreTarget?.tipe] || restoreTarget?.tipe}).
              Sebelum dipulihkan, sistem otomatis membuat backup pengaman (pra-restore) dari kondisi sekarang, sehingga tindakan ini masih bisa dibatalkan dengan memulihkan backup pengaman tersebut.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-button" className="btn-touch">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="restore-confirm-button" onClick={doRestore} disabled={restoring}
              className="bg-amber-600 hover:bg-amber-700 text-white btn-touch">
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Ya, Pulihkan Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!uploadFile} onOpenChange={(open) => !open && setUploadFile(null)}>
        <AlertDialogContent className="bg-white" data-testid="upload-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pulihkan dari File Unggahan?</AlertDialogTitle>
            <AlertDialogDescription>
              Seluruh data saat ini akan <b>DIGANTI</b> dengan isi file <b>{uploadFile?.name}</b> ({uploadFile ? fmtSize(uploadFile.size) : ""}).
              File akan diverifikasi terlebih dahulu (hanya file .enc asli dari aplikasi ini yang diterima).
              Sebelum dipulihkan, sistem otomatis membuat backup pengaman (pra-restore) dari kondisi sekarang.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="upload-restore-cancel" className="btn-touch">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="upload-restore-confirm" onClick={doUploadRestore} disabled={uploading}
              className="bg-amber-600 hover:bg-amber-700 text-white btn-touch">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Ya, Pulihkan dari File
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
